import { unwrap, type components } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Checkbox, Icon, Switch, TableSkeletonRows, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { usePermissions } from "../../../../session/permissions";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { NotificationsWorkflowsNote } from "../../../engage/workflows/defaults/default-note";
import { wsKeys, type PreferenceOut } from "../../shared";
import { useInvalidate, useNotificationPreferences } from "../use-settings";
import { CardActions, PanelError, TableScroll } from "./panel-kit";

type Channel = components["schemas"]["NotificationChannel"];
const CHANNELS: readonly Channel[] = ["in_app", "email"];
const COLUMNS = 2 + CHANNELS.length;

/** Toggle one delivery channel of a preference row (order kept stable). */
export function toggleChannel(channels: readonly Channel[], channel: Channel, on: boolean): Channel[] {
  return CHANNELS.filter((c) => (c === channel ? on : channels.includes(c)));
}

function PreferenceRow({ pref, onChange, disabled }: { pref: PreferenceOut; onChange: (next: PreferenceOut) => void; disabled: boolean }) {
  const { t } = useTranslation();
  const label = t(`settings.notifications.types.${pref.type}`);
  const locked = disabled || pref.mandatory;
  return (
    <tr>
      <td>
        <div className="font-semibold">{label}</div>
        <div className="text-xs text-fg-subtle">{t(`settings.notifications.hints.${pref.type}`)}</div>
        {pref.mandatory ? (
          <Badge className="mt-1">
            <Icon name="lock" className="size-3" />
            {t("settings.notifications.required")}
          </Badge>
        ) : null}
      </td>
      <td>
        <Switch checked={pref.enabled} disabled={locked} aria-label={t("settings.notifications.enableOne", { type: label })} onCheckedChange={(enabled) => { onChange({ ...pref, enabled }); }} />
      </td>
      {CHANNELS.map((channel) => (
        <td key={channel} className="text-center">
          <span className="inline-flex justify-center">
            <Checkbox
              checked={pref.channels.includes(channel)}
              disabled={locked || !pref.enabled}
              aria-label={t("settings.notifications.channelOne", { type: label, channel: t(`settings.notifications.channels.${channel}`) })}
              onCheckedChange={(checked) => { onChange({ ...pref, channels: toggleChannel(pref.channels, channel, checked === true) }); }}
            />
          </span>
        </td>
      ))}
    </tr>
  );
}

function PreferencesCard({ initial, pending }: { initial: readonly PreferenceOut[]; pending: boolean }) {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const message = useErrorMessage();
  const invalidate = useInvalidate();
  const readOnly = usePermissions().readOnly;
  const [draft, setDraft] = useState<PreferenceOut[]>([...initial]);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const preferences = draft.filter((p) => !p.mandatory).map((p) => ({ type: p.type, enabled: p.enabled, channels: p.channels }));
      await unwrap(api.PUT("/v1/notification-preferences", { body: { preferences } }));
      toast.success(t("settings.notifications.saved"));
      await invalidate(wsKeys.notificationPrefs(wsId));
    } catch (caught) {
      toast.error(message(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card overflow-hidden" aria-labelledby="notifications-title">
      <div className="card-header">
        <div>
          <h2 id="notifications-title" className="card-title">
            {t("settings.notifications.title")}
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("settings.notifications.subtitle")}</p>
        </div>
      </div>
      <div className="px-5 pb-3">
        <NotificationsWorkflowsNote />
      </div>
      <TableScroll label={t("settings.notifications.title")}>
        <table className="table">
          <thead>
            <tr>
              <th>{t("settings.notifications.colType")}</th>
              <th className="w-20">{t("settings.notifications.colOn")}</th>
              {CHANNELS.map((channel) => (
                <th key={channel} className="w-24 text-center">
                  {t(`settings.notifications.channels.${channel}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pending ? (
              <TableSkeletonRows rows={6} columns={COLUMNS} />
            ) : (
              draft.map((pref) => <PreferenceRow key={pref.type} pref={pref} disabled={readOnly} onChange={(next) => { setDraft((rows) => rows.map((r) => (r.type === next.type ? next : r))); }} />)
            )}
          </tbody>
        </table>
      </TableScroll>
      <CardActions>
        <Button disabled={readOnly || pending} loading={busy} onClick={() => void save()}>
          {t("settings.save")}
        </Button>
      </CardActions>
    </section>
  );
}

/** Notifications panel (DEC-010 "Reports"): per-type on/off + channels for the signed-in member. */
export function NotificationsPanel() {
  const prefs = useNotificationPreferences();
  if (prefs.isError) return <PanelError error={prefs.error} onRetry={() => void prefs.refetch()} />;
  return <PreferencesCard key={prefs.dataUpdatedAt} initial={prefs.data ?? []} pending={prefs.isPending} />;
}
